<template>
  <v-dialog v-model="dialog" persistent max-width="1200" scrollable>
    <v-card class="modern-editor-dialog" flat>
      <!-- Header with gradient background -->
      <v-card-title class="dialog-header gradient-bg">
        <div class="header-content">
          <h2 class="editor-title white--text">
            <v-icon large color="white" class="mr-3">mdi-hand-heart</v-icon>
            Edit Volunteer Opportunity
          </h2>
          <v-btn icon dark @click="onClose" class="close-btn">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </div>
      </v-card-title>

      <!-- Main content area -->
      <v-card-text class="editor-content">
        <v-container fluid>
          <v-row>
            <!-- Left Column -->
            <v-col cols="12" md="6">
              <!-- Basic Information Section -->
              <v-card flat class="section-card mb-6">
                <v-card-title class="section-title">
                  <v-icon left color="#084468">mdi-information-outline</v-icon>
                  Basic Information
                </v-card-title>
                <v-card-text>
                  <v-select
                    :items="statusOptions"
                    v-model="need.status"
                    label="Status*"
                    outlined
                    dense
                    prepend-icon="mdi-shield-check-outline"
                    :rules="[rules.required]"
                    class="modern-input"
                  />

                  <v-text-field
                    v-model="need.title"
                    label="Opportunity Title*"
                    outlined
                    dense
                    prepend-icon="mdi-format-title"
                    :rules="[rules.required]"
                    :error-messages="titleMessage"
                    @input="onChange"
                    class="modern-input"
                  />

                  <v-textarea
                    v-model="need.description"
                    label="Detailed Description"
                    outlined
                    dense
                    rows="3"
                    auto-grow
                    prepend-icon="mdi-text-box-outline"
                    class="modern-input"
                  />

                  <v-select
                    :items="privacyItems"
                    v-model="need.privacy"
                    label="Visibility*"
                    outlined
                    dense
                    prepend-icon="mdi-lock-outline"
                    :rules="[rules.required]"
                    class="modern-input"
                  />

                  <v-select
                    :items="durationItems"
                    v-model="need.duration"
                    label="Duration Type*"
                    outlined
                    dense
                    prepend-icon="mdi-clock-outline"
                    :error-messages="durationMessage"
                    @input="onChange"
                    disabled
                    class="modern-input"
                  />
                </v-card-text>
              </v-card>

              <!-- Volunteer Requirements Section -->
              <v-card flat class="section-card mb-6">
                <v-card-title class="section-title">
                  <v-icon left color="#084468">mdi-account-group</v-icon>
                  Volunteer Requirements
                </v-card-title>
                <v-card-text>
                  <v-select
                    :items="allowTeamOptions"
                    v-model="need.allow_team"
                    label="Team Registration"
                    outlined
                    dense
                    prepend-icon="mdi-account-multiple-plus"
                    class="modern-input"
                  />

                  <div class="age-range-container">
                    <h4 class="section-subtitle">Age Requirements*</h4>
                    <div class="age-slider-container">
                      <v-range-slider
                        v-model="ageRange"
                        :max="99"
                        :min="1"
                        thumb-label="always"
                        class="age-slider"
                        hide-details
                      >
                        <template v-slot:thumb-label="{ value }">
                          {{ value === 99 ? `${value}+` : value }}
                        </template>
                      </v-range-slider>
                    </div>
                    <div class="age-inputs">
                      <v-text-field
                        v-model="need.min_age"
                        label="Min Age"
                        outlined
                        dense
                        type="number"
                        class="age-input"
                        :error-messages="minAgeMessage"
                        @input="onMinAgeInput($event)"
                      />
                      <span class="age-separator">to</span>
                      <v-text-field
                        v-model="need.max_age"
                        label="Max Age"
                        outlined
                        dense
                        type="number"
                        class="age-input"
                        :error-messages="maxAgeMessage"
                        @input="onMaxAgeInput($event)"
                      />
                    </div>
                  </div>

                  <h4 class="section-subtitle mt-6">Accessibility</h4>
                  <div class="toggle-group">
                    <v-switch
                      v-model="need.family_friendly"
                      label="Family Friendly"
                      color="primary"
                      :true-value="'Yes'"
                      :false-value="'No'"
                      hide-details
                      inset
                    />
                    <v-switch
                      v-model="need.outdoors"
                      label="Outdoor Activity"
                      color="primary"
                      :true-value="'Yes'"
                      :false-value="'No'"
                      hide-details
                      inset
                    />
                    <v-switch
                      v-model="need.wheelchair_accessible"
                      label="Wheelchair Accessible"
                      color="primary"
                      :true-value="'Yes'"
                      :false-value="'No'"
                      hide-details
                      inset
                    />
                    <v-switch
                      v-model="need.virtual_need"
                      label="Virtual Opportunity"
                      color="primary"
                      :true-value="'Yes'"
                      :false-value="'No'"
                      hide-details
                      inset
                    />
                  </div>
                </v-card-text>
              </v-card>
            </v-col>

            <!-- Right Column -->
            <v-col cols="12" md="6">
              <!-- Location Section -->
              <v-card flat class="section-card mb-6">
                <v-card-title class="section-title ">
                  <v-icon left color="#084468">mdi-map-marker-radius</v-icon>
                  Location
                </v-card-title>
                <v-card-text>
                  <v-text-field
                    v-model="need.address"
                    label="Address*"
                    outlined
                    dense
                    prepend-icon="mdi-map-marker"
                    :error-messages="addressMessage"
                    @input="onChange"
                    disabled
                    class="modern-input"
                  />

                  <v-row>
                    <v-col cols="6">
                      <v-text-field
                        v-model="need.city"
                        label="City*"
                        outlined
                        dense
                        prepend-icon="mdi-city"
                        :error-messages="cityMessage"
                        @input="onChange"
                        disabled
                        class="modern-input"
                      />
                    </v-col>
                    <v-col cols="6">
                      <v-select
                        v-model="need.state"
                        :items="stateOptions"
                        label="State*"
                        outlined
                        dense
                        prepend-icon="mdi-earth"
                        :error-messages="stateMessage"
                        @input="onChange"
                        disabled
                        class="modern-input"
                      />
                    </v-col>
                  </v-row>
                  <v-row>
                    <v-col cols="6">
                      <v-text-field
                        v-model="need.zip"
                        label="Zip Code*"
                        outlined
                        dense
                        prepend-icon="mdi-postage-stamp"
                        :error-messages="zipMessage"
                        @input="onChange"
                        :rules="[rules.required, rules.zip]"
                        disabled
                        class="modern-input"
                      />
                    </v-col>
                  </v-row>
                </v-card-text>
              </v-card>

              <!-- Interests Section -->
              <v-card flat class="section-card mb-6 interests-section">
                <v-card-title class="section-title">
                  <v-icon left color="#084468">mdi-heart</v-icon>
                  Interests & Categories
                </v-card-title>
                <v-card-text>
                  <v-select
                    :items="interests"
                    item-text="name"
                    item-value="name"
                    multiple
                    chips
                    deletable-chips
                    hide-selected
                    v-model="need.interests"
                    label="Interests"
                    :class="$vuetify.breakpoint.xs ? 'w-100' : 'ml-5'"
                    auto-select-first
                    style="width: 400px"
                    class="selected-chip"
                    @input="onChangeInterest"
                    :error-messages="interestsMessage"
                  >
                    <template v-slot:selection="{ attrs, item, selected }">
                      <v-chip
                        v-bind="attrs"
                        :color="`${item.color} lighten-3`"
                        :input-value="selected"
                        small
                      >
                        <span class="pr-2">
                          {{ item }}
                        </span>
                        <v-icon small @click="onSelectPrimaryInterest(item)">
                          {{
                            item == need.primaryInterest
                              ? "mdi-star"
                              : "mdi-star-outline"
                          }}
                        </v-icon>
                        <v-icon small @click="deleteInterestItem(item)">
                          $delete
                        </v-icon>
                      </v-chip>
                    </template>
                  </v-select>
                  <div
                    class="mt-2"
                    v-if="need.interests && need.interests.length > 0"
                  >
                    <small class="text--secondary"
                      >Tip: Click the star to set a primary interest.</small
                    >
                  </div>
                </v-card-text>
              </v-card>

              <!-- Media & Finalization Section -->
              <v-card flat class="section-card">
                <v-card-title class="section-title ">
                  <v-icon left color="#084468">mdi-image-multiple</v-icon>
                  Media & Finalization
                </v-card-title>
                <v-card-text>
                  <h4 class="section-subtitle">Opportunity Images</h4>
                  <need-photo-viewer
                    :editable="true"
                    :image-data="need.images"
                    :on-updated-image="onUpdatedImage"
                    class="photo-viewer"
                  />

                  <div class="finalization-options mt-6">
                    <!-- <v-switch
                      v-model="need.auto_accept"
                      label="Auto-accept volunteer responses"
                      color="primary"
                      :true-value="'Yes'"
                      :false-value="'No'"
                      hide-details
                      inset
                    /> -->
                    <v-switch
                      v-model="need.cori_report"
                      label="Require CORI background check"
                      color="primary"
                      :true-value="'Yes'"
                      :false-value="'No'"
                      hide-details
                      inset
                    />
                  </div>
                </v-card-text>
              </v-card>
            </v-col>
          </v-row>
        </v-container>
      </v-card-text>

      <!-- Footer with action buttons -->
      <v-divider></v-divider>
      <v-card-actions class="editor-actions">
        <v-spacer></v-spacer>
        <v-btn text @click="onClose" class="mr-3">Cancel</v-btn>
        <v-btn
          color="#084468"
          depressed
          :loading="loading"
          @click="onSave"
          class="save-btn"
        >
          Save Changes
        </v-btn>
      </v-card-actions>

      <v-snackbar v-model="snackbar" :timeout="4000" color="primary" top right>
        {{ snackMessage }}
        <template v-slot:action="{ attrs }">
          <v-btn text v-bind="attrs" @click="snackbar = false"> Close </v-btn>
        </template>
      </v-snackbar>
    </v-card>
  </v-dialog>
</template>

<script>
import DOMPurify from "dompurify";
import NeedPhotoViewer from "@/components/agency/NeedPhotoViewer.vue";
import { mapActions, mapState } from "vuex";

export default {
  components: {
    NeedPhotoViewer,
  },
  props: {
    dialog: {
      type: Boolean,
      default: false,
    },
    onCloseDialog: {
      type: Function,
    },
    item: Object,
  },
  data() {
    return {
      ageRange: [1, 120],
      error: null,
      rules: {
        required: (value) => !!value || "Required.",
        zip: (value) => /^\d{5}(-\d{4})?$/.test(value) || "Invalid zip code.",
      },
      loading: false,
      privacyItems: ["Public", "Private"],
      durationItems: [
        "Is Ongoing",
        "Runs Until",
        "Happens On",
        "Multi-Date",
        "Custom Shifts",
        "Recurring Shifts",
      ],
      allowTeamOptions: ["No", "Yes", "Teams only"],
      statusOptions: ["Active", "Pending", "Inactive"],
      stateOptions: [
        "AL",
        "AK",
        "AZ",
        "AR",
        "CA",
        "CO",
        "CT",
        "DE",
        "FL",
        "GA",
        "HI",
        "ID",
        "IL",
        "IN",
        "IA",
        "KS",
        "KY",
        "LA",
        "ME",
        "MD",
        "MA",
        "MI",
        "MN",
        "MS",
        "MO",
        "MT",
        "NE",
        "NV",
        "NH",
        "NJ",
        "NM",
        "NY",
        "NC",
        "ND",
        "OH",
        "OK",
        "OR",
        "PA",
        "RI",
        "SC",
        "SD",
        "TN",
        "TX",
        "UT",
        "VT",
        "VA",
        "WA",
        "WV",
        "WI",
        "WY",
        "AS",
        "DC",
        "FM",
        "GU",
        "MH",
        "MP",
        "PW",
        "PR",
        "VI",
      ],
      snackbar: false,
      snackMessage: null,
      titleMessage: null,
      durationMessage: null,
      zipMessage: null,
      interestsMessage: null,
      need: {},
      addressMessage: null,
      cityMessage: null,
      stateMessage: null,
      minAgeMessage: null,
      maxAgeMessage: null,
    };
  },
  computed: {
    ...mapState("icon", {
      allInterests: "interestNames",
    }),
    ...mapState("icon", {
      interests: "interestNames",
    }),
    filteredInterests() {
      return this.allInterests.filter(
        (interest) =>
          !this.need.interests || !this.need.interests.includes(interest.name)
      );
    },
    allInterestNames() {
      // If your interests are objects with a 'name' property
      return this.allInterests;
    },
  },
  watch: {
    ageRange(newVal) {
      this.need.min_age = newVal[0];
      this.need.max_age = newVal[1];
    },
    item(newValue) {
      this.need = { ...newValue };
      if (this.need.min_age && this.need.max_age) {
        this.ageRange = [this.need.min_age, this.need.max_age];
      }
    },
    dialog() {
      if (this.dialog) {
        this.need = { ...this.item };
        if (this.need.min_age && this.need.max_age) {
          this.ageRange = [this.need.min_age, this.need.max_age];
        }
      }
    },
  },
  methods: {
    ...mapActions({
      editNeed: "need/editNeed",
      getInterestIcons: "icon/getInterestIcons",
    }),
    onClose() {
      this.need = {
        first_name: "",
        last_name: "",
        email: "",
        phone: "",
        address: "",
        address2: "",
        end_date: "",
        images: [],
      };
      this.onCloseDialog();
    },
    isInterestSelected(interest) {
      return this.need.interests && this.need.interests.includes(interest.name);
    },
    toggleInterest(interest) {
      if (!this.need.interests) {
        this.need.interests = [];
      }

      const index = this.need.interests.indexOf(interest.name);
      if (index === -1) {
        this.need.interests.push(interest.name);
        if (!this.need.primaryInterest) {
          this.need.primaryInterest = interest.name;
        }
      } else {
        this.need.interests.splice(index, 1);
        if (this.need.primaryInterest === interest.name) {
          this.need.primaryInterest = this.need.interests[0] || null;
        }
      }
    },
    
    removeInterest(interestName) {
      const idx = this.need.interests.indexOf(interestName);
      if (idx !== -1) {
        this.need.interests.splice(idx, 1);
        if (this.need.primaryInterest === interestName) {
          this.need.primaryInterest = this.need.interests[0] || null;
        }
      }
    },
    setPrimaryInterest(interestName) {
      this.need.primaryInterest = interestName;
    },
    isPrimaryInterest(interestName) {
      return this.need.primaryInterest === interestName;
    },
    getInterestColor(interestName) {
      const interest = this.allInterests.find((i) => i.name === interestName);
      return interest ? interest.color : "primary";
    },
    getPrimaryInterestIcon() {
      if (!this.need.primaryInterest) return "mdi-heart";
      const interest = this.allInterests.find(
        (i) => i.name === this.need.primaryInterest
      );
      return interest ? interest.icon : "mdi-heart";
    },
    onMinAgeInput(value) {
      const numericValue = value.replace(/[^0-9]/g, "");
      if (numericValue === "" || Number(numericValue) < 1) {
        this.need.min_age = 1;
        this.ageRange = [1, this.ageRange[1]];
      } else {
        this.need.min_age = Number(numericValue);
        this.ageRange = [Number(numericValue), this.ageRange[1]];
      }
    },
    onMaxAgeInput(value) {
      const numericValue = value.replace(/[^0-9]/g, "");
      if (numericValue === "" || Number(numericValue) < 1) {
        this.need.max_age = 1;
        this.ageRange = [this.ageRange[0], 1];
      } else {
        this.need.max_age = Number(numericValue);
        this.ageRange = [this.ageRange[0], Number(numericValue)];
      }
    },
    deleteInterestItem(item) {
      if (this.need.primaryInterest == item) {
        this.need.primaryInterest =
          this.need.interests.find((it) => it != item) || null;
      }
      this.need.interests = this.need.interests.filter((it) => it != item);
    },
    onChange() {
      this.error = null;
      this.titleMessage = null;
      this.durationMessage = null;
      this.zipMessage = null;
      this.interestsMessage = null;
      this.addressMessage = null;
      this.cityMessage = null;
      this.minAgeMessage = null;
      this.maxAgeMessage = null;
      this.stateMessage = null;
    },
    onUpdatedImage(images) {
      this.need.images = images;
      this.onChange();
    },
    onChangeInterest() {
      this.onChange();
      if (
        this.need.interests &&
        this.need.interests.length > 0 &&
        !this.need.primaryInterest
      ) {
        this.need.primaryInterest = this.need.interests[0];
      }
    },
        // Utility sanitizer
    sanitizeInput(value) {
      if (!value) return value;

      // Step 1: Sanitize unsafe HTML but allow text inside <script> to survive
      let clean = DOMPurify.sanitize(value, {
        ALLOWED_TAGS: [],
        ALLOWED_ATTR: [],
      });

      // Step 2: If DOMPurify returns empty string, fallback to extracting inner text
      if (!clean.trim()) {
        // Create a temporary div to extract text content
        const temp = document.createElement("div");
        temp.innerHTML = value;
        clean = temp.textContent || temp.innerText || "";
      }

      return clean.trim();
    },
        // Clean the entire "need" object before saving
    cleanNeed(need) {
      return {
        ...need,
        title: this.sanitizeInput(need.title),
        description: this.sanitizeInput(need.description),
        hours: this.sanitizeInput(need.hours),
        hours_description: this.sanitizeInput(need.hours_description),
        address: this.sanitizeInput(need.address),
        city: this.sanitizeInput(need.city),
        state: this.sanitizeInput(need.state),
        zip: this.sanitizeInput(need.zip),
        attributes: (need.attributes || []).map((attr) =>
          this.sanitizeInput(attr)
        ),
        interests: (need.interests || []).map((i) => this.sanitizeInput(i)),
      };
    },
    async onSave() {
      this.error = null;
      this.titleMessage = null;
      this.zipMessage = null;
      this.durationMessage = null;
      this.minAgeMessage = null;
      this.maxAgeMessage = null;
      if (!this.need.title) {
        this.titleMessage = "required";
        this.$vuetify.goTo(
          this.$refs.title,
          {
            container: this.$refs.needContent,
          },
          500
        );
        return;
      }
      if (!this.need.duration) {
        this.durationMessage = "required";
        this.$vuetify.goTo(
          this.$refs.duration,
          {
            container: this.$refs.needContent,
          },
          500
        );
        return;
      }
      if (!this.need.min_age) {
        this.minAgeMessage = "Minimum age is required";
        this.$vuetify.goTo(
          this.$refs.min_age,
          {
            container: this.$refs.needContent,
          },
          500
        );
        return;
      }
      if (!this.need.max_age) {
        this.maxAgeMessage = "Maximum age is required";
        this.$vuetify.goTo(
          this.$refs.max_age,
          {
            container: this.$refs.needContent,
          },
          500
        );
        return;
      }
      if (this.need.min_age && this.need.max_age) {
        if (parseInt(this.need.min_age) > parseInt(this.need.max_age)) {
          this.minAgeMessage = "Minimum age must be less than maximum age";
          this.$vuetify.goTo(
            this.$refs.min_age,
            {
              container: this.$refs.needContent,
            },
            500
          );
          return;
        }
      }
      if (this.need.min_age > 120) {
        this.minAgeMessage = "Minimum age must be less than 120";
        this.$vuetify.goTo(
          this.$refs.min_age,
          {
            container: this.$refs.needContent,
          },
          500
        );
        return;
      }
      if (this.need.max_age > 120) {
        this.maxAgeMessage = "Maximum age must be less than 120";
        this.$vuetify.goTo(
          this.$refs.max_age,
          {
            container: this.$refs.needContent,
          },
          500
        );
        return;
      }
      if (this.need.duration != "Custom Shifts" && !this.need.hours) {
        this.hoursMessage = "required";
        this.$vuetify.goTo(
          this.$refs.sub.$refs.hours,
          {
            container: this.$refs.needContent,
          },
          500
        );

        return;
      }
      if (!this.need.zip) {
        this.zipMessage = "required";
        this.$vuetify.goTo(
          this.$refs.zip,
          {
            container: this.$refs.needContent,
          },
          500
        );
        return;
      }
      if (!this.need.interests || this.need.interests.length == 0) {
        this.interestsMessage = "required";
        this.$vuetify.goTo(
          this.$refs.interests,
          {
            container: this.$refs.needContent,
          },
          500
        );
        return;
      }
      // if (!this.need.address) {
      //   this.addressMessage = "Address is required";
      //   this.$vuetify.goTo(
      //     this.$refs.address,
      //     {
      //       container: this.$refs.needContent
      //     },
      //     500
      //   );
      //   return;
      // }
      // if (!this.need.city) {
      //   this.cityMessage = "City is required";
      //   this.$vuetify.goTo(
      //     this.$refs.city,
      //     {
      //       container: this.$refs.needContent
      //     },
      //     500
      //   );
      //   return;
      // }
      // if (!this.need.state) {
      //   this.stateMessage = "State is required";
      //   this.$vuetify.goTo(
      //     this.$refs.state,
      //     {
      //       container: this.$refs.needContent
      //     },
      //     500
      //   );
      //   return;
      // }
      // if (!this.need.zip) {
      //   this.zipMessage = "Zip code is required";
      //   this.$vuetify.goTo(
      //     this.$refs.zip,
      //     {
      //       container: this.$refs.needContent
      //     },
      //     500
      //   );
      //   return;
      // } else if (!this.rules.zip(this.need.zip)) {
      //   this.zipMessage = "Invalid zip code";
      //   this.$vuetify.goTo(
      //     this.$refs.zip,
      //     {
      //       container: this.$refs.needContent
      //     },
      //     500
      //   );
      //   return;
      // }
    const safeNeed = this.cleanNeed(this.need);
      this.loading = true;
            try {
        await this.editNeed(safeNeed);
        this.loading = false;
        this.onClose();
      } catch (error) {
          this.loading = false;
          this.error = error.response.data.message;
      }
    },
  },
  mounted() {
    this.need = { ...this.item };
    if (this.need.min_age && this.need.max_age) {
      this.ageRange = [this.need.min_age, this.need.max_age];
    }
    this.getInterestIcons().catch((error) => console.log(error));
  },
};
</script>

<style scoped lang="scss">
.modern-editor-dialog {
  border-radius: 12px !important;
  overflow: hidden;

  .dialog-header {
    padding: 20px 30px;
    position: relative;

    .header-content {
      display: flex;
      justify-content: space-between;
      align-items: center;
      width: 100%;
    }

    .editor-title {
      font-weight: 600;
      font-size: 1.5rem;
      display: flex;
      align-items: center;
    }

    .close-btn {
      position: absolute;
      right: 15px;
      top: 15px;
    }
  }

  .gradient-bg {
    background: linear-gradient(135deg, #084468 0%, #3b7ca1 100%) !important;
  }

  .editor-content {
    padding: 0;
    max-height: 70vh;
    overflow-y: auto;

    @media (max-width: 960px) {
      max-height: 60vh;
    }

    &::-webkit-scrollbar {
      width: 6px;
    }

    &::-webkit-scrollbar-track {
      background: #f1f1f1;
      border-radius: 3px;
    }

    &::-webkit-scrollbar-thumb {
      background: #888;
      border-radius: 3px;
    }

    &::-webkit-scrollbar-thumb:hover {
      background: #555;
    }
  }

  .section-card {
    border-radius: 8px;
    box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
    padding: 20px;
    margin-bottom: 24px;

    .section-title {
      font-size: 1.1rem;
      font-weight: 600;
      padding: 0 0 16px 0;
      display: flex;
      align-items: center;
    }

    .section-subtitle {
      font-size: 0.875rem;
      font-weight: 500;
      color: rgba(0, 0, 0, 0.6);
      margin-bottom: 12px;
    }
  }

  .modern-input {
    margin-bottom: 16px;

    .v-input__prepend-outer {
      margin-right: 12px;
    }
  }

  .age-range-container {
    margin-bottom: 24px;

    .age-slider-container {
      padding: 0 12px;

      .age-slider {
        margin-top: 24px;
      }
    }

    .age-inputs {
      display: flex;
      align-items: center;
      gap: 16px;
      margin-top: 16px;

      .age-input {
        flex: 1;
      }

      .age-separator {
        color: rgba(0, 0, 0, 0.54);
        font-size: 0.875rem;
      }
    }
  }

  .toggle-group {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  .map-preview {
    height: 200px;
    background-color: #f5f5f5;
    border-radius: 8px;
    margin-bottom: 20px;
    display: flex;
    justify-content: center;
    align-items: center;
    flex-direction: column;
    color: #757575;

    .map-placeholder {
      text-align: center;

      p {
        margin-top: 10px;
        font-size: 0.875rem;
      }
    }
  }

  .interests-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
    gap: 8px;
    margin-bottom: 20px;

    .interest-chip {
      cursor: pointer;
      transition: all 0.2s ease;
      justify-content: flex-start;

      &.selected-interest {
        background-color: rgba(63, 81, 181, 0.1);
        border-color: #084468;
      }

      &:hover {
        transform: translateY(-2px);
        box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
      }
    }
  }

  .interests-section {
    background: #f8fafc;
    .section-title {
      margin-bottom: 8px;
    }
    .interests-grid {
      display: flex;
      flex-wrap: wrap;
      gap: 8px;
      margin-bottom: 12px;
      .interest-chip {
        cursor: pointer;
        transition: all 0.2s;
        font-size: 0.95em;
        &.selected-interest {
          box-shadow: 0 2px 8px rgba(3, 27, 105, 0.546);
          font-weight: 600;
        }
        &:hover {
          filter: brightness(0.95);
          transform: translateY(-2px) scale(1.04);
          z-index: 2;
        }
      }
    }
    .selected-interests {
      .selected-title {
        font-size: 0.95em;
        font-weight: 500;
        color: #1976d2;
        margin-bottom: 8px;
      }
      .selected-chips-row {
        display: flex;
        flex-wrap: wrap;
        gap: 8px;
        margin-bottom: 8px;
      }
      .selected-chip {
        margin-bottom: 4px;
        .star-icon {
          cursor: pointer;
          &:hover {
            color: #ffd600 !important;
          }
        }
      }
      .no-interests {
        color: #aaa;
        font-size: 0.9em;
        font-style: italic;
      }
    }
  }

  .photo-viewer {
    border: 1px dashed #ddd;
    border-radius: 8px;
    padding: 16px;
  }

  .preview-card {
    border: 1px solid #e0e0e0;
    border-radius: 8px;
    padding: 20px;
    text-align: center;

    .preview-title {
      font-size: 1rem;
      font-weight: 500;
      margin-bottom: 16px;
      color: rgba(0, 0, 0, 0.6);
    }

    .preview-content {
      .preview-icon {
        margin-bottom: 12px;
      }

      .preview-name {
        font-size: 1.25rem;
        font-weight: 500;
        margin-bottom: 8px;
      }

      .preview-description {
        color: rgba(0, 0, 0, 0.6);
        font-size: 0.875rem;
        margin-bottom: 16px;
      }

      .preview-tags {
        display: flex;
        justify-content: center;
        gap: 8px;
        flex-wrap: wrap;
      }
    }
  }

  .editor-actions {
    padding: 16px 24px;

    .save-btn {
      font-weight: 600;
      letter-spacing: 0.5px;
      padding: 8px 24px;
      box-shadow: 0 2px 10px rgba(63, 81, 181, 0.3);
      color: #ffff;
      border-radius: 20px;
    }
  }
}

@media (max-width: 960px) {
  .modern-editor-dialog {
    .dialog-header {
      padding: 16px;

      .editor-title {
        font-size: 1.25rem;
      }
    }

    .section-card {
      padding: 16px;
    }
  }
}
</style>